import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const fmt = (n) =>
  (n || n === 0)
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const HsnSummaryView = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [fromDate, setFromDate] = useState(firstOfMonth.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/reports/hsn-summary`, {
        headers: { Authorization: token },
        params: { from: fromDate, to: toDate },
      });
      setResults(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/unauthorized");
        return;
      }
      console.error("Failed to load HSN summary", err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = results.reduce(
    (acc, r) => ({
      qty: acc.qty + (parseFloat(r.totalQty) || 0),
      value: acc.value + (parseFloat(r.totalValue) || 0),
      cgst: acc.cgst + (parseFloat(r.totalCgst) || 0),
      sgst: acc.sgst + (parseFloat(r.totalSgst) || 0),
      igst: acc.igst + (parseFloat(r.totalIgst) || 0),
    }),
    { qty: 0, value: 0, cgst: 0, sgst: 0, igst: 0 }
  );

  const handleExportCsv = () => {
    if (!results.length) return;
    const lines = [];
    lines.push(`HSN Summary — ${fromDate} to ${toDate}`);
    lines.push("");
    lines.push("HSN Code,Unit,Total Qty,Taxable Value,CGST,SGST,IGST");
    results.forEach((r) => {
      lines.push(`${r.hsnCode},${r.uqc},${r.totalQty},${r.totalValue},${r.totalCgst},${r.totalSgst},${r.totalIgst}`);
    });
    lines.push(`TOTAL,,${totals.qty},${totals.value},${totals.cgst},${totals.sgst},${totals.igst}`);

    const csvContent = lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hsn_summary_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cellStyle = { border: "1px solid #eee", padding: "10px 14px", fontSize: "13px" };
  const thStyle = { ...cellStyle, background: "#f5f6fa", fontWeight: "600", textAlign: "left", color: "#555" };

  return (
    <div style={{ background: "#f5f6fa", padding: "20px", borderRadius: "8px" }}>
      {/* ── Date range picker + Export ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
            />
          </div>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={fetchSummary} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>
        <button className="btn btn-danger btn-sm" onClick={handleExportCsv} disabled={!results.length}>
          <i className="fa-solid fa-download mr-1"></i> Export CSV
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading HSN summary...</div>
      ) : (
        <>
          {/* ── Summary cards ── */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
            {[
              { label: "Taxable Value", value: `₹${fmt(totals.value)}`, color: "#1976d2" },
              { label: "Total CGST", value: `₹${fmt(totals.cgst)}`, color: "#27ae60" },
              { label: "Total SGST", value: `₹${fmt(totals.sgst)}`, color: "#27ae60" },
              { label: "Total IGST", value: `₹${fmt(totals.igst)}`, color: "#c0392b" },
            ].map((card) => (
              <div key={card.label} style={{
                flex: 1, minWidth: "160px", background: "#fff",
                borderRadius: "10px", padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                borderTop: `3px solid ${card.color}`,
              }}>
                <div style={{ fontSize: "11px", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {card.label}
                </div>
                <div style={{ fontSize: "22px", fontWeight: "700", color: "#222", marginTop: "6px" }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── HSN-wise breakdown table ── */}
          <div style={{ background: "#fff", borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px" }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#333", marginBottom: "12px" }}>
              HSN-wise Breakdown — {fromDate} to {toDate}
            </div>
            {results.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px", color: "#aaa", fontSize: "13px" }}>
                No sales found for this date range.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>HSN Code</th>
                    <th style={thStyle}>Unit</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Total Qty</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Taxable Value</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>CGST</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>SGST</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>IGST</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.hsnCode}>
                      <td style={{ ...cellStyle, fontWeight: "600" }}>{row.hsnCode}</td>
                      <td style={cellStyle}>{row.uqc}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>{fmt(row.totalQty)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.totalValue)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.totalCgst)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.totalSgst)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.totalIgst)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f5f6fa" }}>
                    <td style={{ ...cellStyle, fontWeight: "700" }} colSpan={2}>TOTAL</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>{fmt(totals.qty)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(totals.value)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(totals.cgst)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(totals.sgst)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(totals.igst)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* ── Note ── */}
          <div style={{ fontSize: "12px", color: "#888", background: "#fffbea", border: "1px solid #f0e5b8", borderRadius: "8px", padding: "12px 16px" }}>
            <i className="fa-solid fa-circle-info mr-1"></i>
            This HSN-wise summary is used for GSTR-1 Table 12. Always cross-check against your actual invoices before filing.
          </div>
        </>
      )}
    </div>
  );
};

export default HsnSummaryView;